import React, { FC, useState } from 'react';
import { EditOutlined, FolderOpenOutlined, SearchOutlined } from '@ant-design/icons';
import colors from '../../utils/colors';
import {
  ACCESS_UNTIL,
  CREATE_ORGANIZATION,
  EMAIL_DOMAIN,
  ID,
  LOGO,
  NAME,
  ORGANIZATIONS,
  REGISTRATION_LINK,
  SEARCH_BY_NAME,
  SENDY_LIST_ID
} from '../../utils/constants/labels';
import CreateOrganizationForm from './CreateOrganizationForm';
import CreationModal from '../common/modal/CreationModal';
import PageHeader from '../common/layout/PageHeader';
import Table from '../common/table/Table';
import { api } from '../../api/api';
import { Button, Input, Tooltip } from 'antd';
import { debounce } from '../../utils/common';
import { DATE_FORMAT, formatDate, toMomentDate } from '../../utils/timeUtils';
import { useHistory } from 'react-router';
import * as routes from '../../utils/constants/routes';
import { handleErrorResponse } from '../../api/network';
import { DatePreview, NotificationType, notify } from '../../utils/helpers';
import styled from 'styled-components';
import EditModal from '../common/modal/EditModal';
import { OrganizationDto } from '../../types/userTypes';

type Props = {};

const Organizations: FC<Props> = () => {
  const [name, setName] = useState('');
  const [refreshTable, setRefreshTable] = useState(null);
  const [editedOrganization, setEditedOrganization] = useState<OrganizationDto | null>(null);
  const history = useHistory();

  const setNameDebounced = debounce(value => setName(value), 400);

  const columns = [
    {
      title: ID,
      dataIndex: 'id',
      sorter: true,
      width: '5%',
      defaultSortOrder: 'descend'
    },
    {
      title: LOGO,
      dataIndex: 'logo',
      width: '10%',
      render: (value, row, index) => <img src={row.logoUrl} height={40} />
    },
    {
      title: NAME,
      dataIndex: 'name',
      sorter: true,
      width: '20%'
    },
    {
      title: ACCESS_UNTIL,
      dataIndex: 'accessUntil',
      width: '15%',
      render: value => <DatePreview>{formatDate(value, DATE_FORMAT)}</DatePreview>
    },
    {
      title: SENDY_LIST_ID,
      dataIndex: 'sendyListId',
      width: '15%'
    },
    {
      title: REGISTRATION_LINK,
      dataIndex: 'registrationLink',
      width: '15%'
    },
    {
      title: EMAIL_DOMAIN,
      dataIndex: 'emailDomainName',
      width: '15%',
      render: (value, row, index) => (
        value?.split(',').map((d) => <div key={d}>{d}</div>)
      )
    },
    {
      title: '',
      dataIndex: 'open',
      width: '5%',
      render: (value, row, index) => (
        <Tooltip title={'Edit organization'}>
          <Button type="link" icon={<EditOutlined />} onClick={() => setEditedOrganization(row)} />
        </Tooltip>
      )
    },
    {
      title: '',
      dataIndex: 'open',
      width: '5%',
      render: (value, row, index) => (
        <Tooltip title={'Open organization'} placement={'topLeft'}>
          <Button
            type="link"
            onClick={() => history.push(`${routes.DASHBOARD}${routes.ORGANIZATIONS}/${row.id}`, { organization: row })}
            icon={<FolderOpenOutlined />}
          />
        </Tooltip>
      )
    }
  ];

  const isEdit = !!editedOrganization;

  return (
    <Container>
      <PageHeader
        title={ORGANIZATIONS}
        extra={[
          <Input
            key={1}
            onChange={e => setNameDebounced(e.target.value)}
            placeholder={SEARCH_BY_NAME}
            prefix={<SearchOutlined />}
          />,
          <CreationModal
            key={2}
            title={CREATE_ORGANIZATION}
            onCreate={async values => {
              const response = await api.createOrganization(values);
              if (!response.ok) {
                handleErrorResponse(response, history);
                return false;
              }
              notify('Organization successfully created', NotificationType.SUCCESS);
              // @ts-ignore
              await refreshTable();
              return true;
            }}
            ModalForm={CreateOrganizationForm}
            bindActionButtons
          />
        ]}
      />
      <Table
        columns={columns}
        getData={api.getAllOrganizations}
        idPropName={'id'}
        searchPropName={'name'}
        searchPropValue={name}
        bindFetchFn={setRefreshTable}
      />
      {isEdit && (
        <EditModal
          title={'Update organization'}
          visible={isEdit}
          dismiss={() => setEditedOrganization(null)}
          ModalForm={CreateOrganizationForm}
          value={{
            ...editedOrganization,
            accessUntil: editedOrganization!.accessUntil ? toMomentDate(editedOrganization!.accessUntil) : null
          }}
          onSubmit={async values => {
            const response = await api.updateOrganization(editedOrganization!.id, values);
            if (!response.ok) {
              handleErrorResponse(response, history);
              return false;
            }
            notify('Organization successfully updated', NotificationType.SUCCESS);
            // @ts-ignore
            await refreshTable();
            setEditedOrganization(null);
          }}
          bindActionButtons
        />
      )}
    </Container>
  );
};

export default Organizations;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
  background-color: ${colors.white};
  position: relative;
`;
